import React, { useState, useEffect } from "react";
import UploadFile from "../components/UploadFile";
import DarkModeToggle from "../components/DarkModeToggle";
import { useActiveModel } from "../context/ActiveModelContext";
import { useAuth } from "../context/AuthContext";
import "../components/user-menu.css";

export default function DashboardLayout({
  children,
  datasets = [],
  selectedDataset,
  onSelectDataset,
  onSelectDatasetId,
  datasetDetails = [],
  modelTypes = [],
  selectedModel,
  onSelectModel,
  onDataReceived,
  onFeedbackClick,
  onUserDashboardClick,
  onAdminLoginClick,
  onDeleteDataset, 
  onRenameDataset,
}) {
  const { activeModel, clearActiveModel } = useActiveModel();
  const { user } = useAuth();
  const [showUserMenu, setShowUserMenu] = useState(false);

  // Clear active model when user state changes (login/logout)
  useEffect(() => {
    clearActiveModel();
  }, [user, clearActiveModel]);

  return (
    <div className="layout">
      {/* Dark Mode Toggle */}
      <DarkModeToggle />
      
      {/* Sidebar */}
      <aside className="sidebar">
        <div className="sidebar-header">
          <h2>Datasets</h2>
        </div>
        <div className="sidebar-upload">
          <UploadFile onDataReceived={onDataReceived} />
        </div>
        <nav className="sidebar-nav">
          {datasets && datasets.length > 0 ? (
            datasets.map((ds) => (
              <div key={ds} style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
                <button
                  className={`sidebar-item ${selectedDataset === ds ? "active" : ""}`}
                  style={{ flexGrow: 1, margin: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}
                  onClick={() => {
                    if (onSelectDataset) onSelectDataset(ds);
                    if (onSelectDatasetId) {
                      const selectedDetail = datasetDetails.find((item) => item.name === ds);
                      onSelectDatasetId(selectedDetail?.id ?? null);
                    }
                  }}
                >
                  {ds}
                </button>
                
                <button 
                  onClick={() => {
                    const newName = window.prompt(`Rename dataset "${ds}" to:`, ds);
                    if (newName && newName.trim() !== "" && newName !== ds && onRenameDataset) {
                      onRenameDataset(ds, newName.trim());
                    }
                  }}
                  title="Rename dataset"
                  style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '0 4px', fontSize: '8px' }}
                >
                  ✏️
                </button>
                
                <button 
                  onClick={() => {
                    if (window.confirm(`Are you sure you want to delete the dataset "${ds}"?`) && onDeleteDataset) {
                      onDeleteDataset(ds);
                    }
                  }}
                  title="Delete dataset"
                  style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '0 4px', fontSize: '8px' }}
                >
                  ❌
                </button>
              </div>
            ))
          ) : (
            <div className="sidebar-item" style={{ cursor: "default", color: "var(--text-muted)" }}>
              No datasets loaded
            </div>
          )}
        </nav>

        <div className="sidebar-header" style={{ marginTop: 16 }}>
          <h2>Models</h2>
        </div>
        <nav className="sidebar-nav">
          {modelTypes && modelTypes.length > 0 ? (
            modelTypes.map((model) => (
              <button
                key={model}
                className={`sidebar-item ${selectedModel === model ? "active" : ""}`}
                onClick={() => onSelectModel && onSelectModel(model)}
              >
                {model}
              </button>
            ))
          ) : (
            <div className="sidebar-item" style={{ cursor: "default", color: "var(--text-muted)" }}>
              No models available
            </div>
          )}
        </nav>

        {/* Active Model */}
        <div className="sidebar-header" style={{ marginTop: 16 }}>
          <h2>Active Model</h2>
        </div>
        <div className="sidebar-card">
          {activeModel ? (
            <>
              <div className="sidebar-item" style={{ cursor: "default" }}>
                <div><strong>ID:</strong> {activeModel.id}</div>
                <div><strong>Dataset:</strong> {activeModel.dataset_name}</div>
                <div><strong>Type:</strong> {activeModel.model_type}</div>
              </div>
              <button className="sidebar-item" onClick={clearActiveModel}>Clear Active Model</button>
            </>
          ) : (
            <div className="sidebar-item" style={{ cursor: "default", color: "var(--text-muted)" }}>
              None selected
            </div>
          )}
        </div>

        {/* Feedback Button at bottom of sidebar */}
        <div className="sidebar-feedback">
          <button
            onClick={onFeedbackClick}
            className="feedback-button"
            title="Open Feedback & Support"
          >
            <svg 
              width="16" 
              height="16" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth="2" 
                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" 
              />
            </svg>
            Feedback & Support
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="main">
        {/* Header */}
        <header className="header">
          <h1>BioDawn Drug Discovery Dashboard</h1>
          
          {/* Admin Login Button */}
          {!user && (
            <div className="admin-login-section">
              <button
                onClick={onAdminLoginClick}
                className="admin-login-btn"
                title="Admin Login"
              >
                🛠️ Admin
              </button>
            </div>
          )}
          
          {/* User Menu */}
          {user && (
            <div className="user-menu">
              <button
                className="user-menu-button"
                onClick={() => setShowUserMenu(!showUserMenu)}
              >
                <div className="user-avatar">
                  {user.full_name ? user.full_name.charAt(0).toUpperCase() : user.email.charAt(0).toUpperCase()}
                </div>
                <span className="user-name">{user.full_name || user.email}</span>
                <svg 
                  width="16" 
                  height="16" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                  className={`dropdown-arrow ${showUserMenu ? 'open' : ''}`}
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth="2" 
                    d="M19 9l-7 7-7-7" 
                  />
                </svg>
              </button>
              
              {showUserMenu && (
                <div className="user-menu-dropdown">
                  <div className="user-info">
                    <div className="user-email">{user.email}</div>
                    <div className={`subscription-tier tier-${user.subscription_tier}`}>
                      {user.subscription_tier} Plan
                    </div>
                  </div>
                  <div className="menu-divider"></div>
                  <button 
                    className="menu-item"
                    onClick={() => {
                      setShowUserMenu(false);
                      onUserDashboardClick && onUserDashboardClick();
                    }}
                  >
                    <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    Account Settings
                  </button>
                  <button 
                    className="menu-item"
                    onClick={() => {
                      setShowUserMenu(false);
                      onFeedbackClick && onFeedbackClick();
                    }}
                  >
                    <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                    Support
                  </button>
                </div>
              )}
            </div>
          )}
        </header>

        {/* Page Content */}
        <main className="content">{children}</main>

        {/* Footer */}
        <footer className="footer">© 2025 BioDawn Innovations</footer>
      </div>
    </div>
  );
}
